import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { awaitParentMessage, publishParentMessage, parentMessages$ } from './EmbedMessageBus';
import { BootstrapContext, IBootstrapContext } from 'rev-shared/bootstrap/BootstrapContext';
import { updateLocale } from 'rev-shared/bootstrap/LocaleContext';
import { lastValueFrom } from 'rev-shared/rxjs/lastValueFrom';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { filter } from 'rxjs';
import { ISDKAuthChangeRequest, ISDKPostMessage, ISDKToken } from './ISDKContracts';

@Injectable({
	providedIn: 'root'
})
export class EmbedTokenService {
	private initializedPromise: Promise<any>;

	constructor(
		private readonly http: HttpClient,
		private readonly UserContext: UserContextService
	) {
	}

	public init(): Promise<any> {
		if(this.initializedPromise) {
			return this.initializedPromise;
		}

		publishParentMessage('load');

		if(BootstrapContext.tokenParam) {
			const accessToken = BootstrapContext.tokenParam;
			return this.updateUserDetails({ accessToken });
		}

		if(BootstrapContext.tokenParam !== '') {
			return this.initializedPromise = Promise.resolve();
		}

		return this.initializedPromise = awaitParentMessage<ISDKAuthChangeRequest>('authenticated', 'error')
			.then(e => {
				this.subscribeAuthChanged();
				return this.updateUserDetails(e.msg.token);
			})
			.then(() => {
				publishParentMessage('authChanged');
			})
			.catch(e => {
				publishParentMessage('error', { msg: 'Unauthorized' });
				console.error('Error initializing token: ', e);
				return Promise.reject(e);
			});
	}

	private updateUserDetails(token: ISDKToken): Promise<any> {
		if(!token?.accessToken) {
			return Promise.reject('Missing accessToken');
		}
		return lastValueFrom(this.http.get<IBootstrapContext>('/user-context', {
			headers: {
				Authorization: `VBrick ${token.accessToken}`
			}
		}))
			.then(result => {
				if(!result?.user) {
					return Promise.reject('User token not authorized');
				}

				const { user } = result;

				updateLocale(result);

				return this.UserContext.updateUserAndSecurityContext({
					id: user.id,
					email: user.email,
					language: user.language,
					token,
					isRegisteredGuest: user.isRegisteredGuest,
					isPreRegistered: user.isPreRegistered
				});
			});
	}

	private subscribeAuthChanged() {
		parentMessages$.pipe(
			filter(m => m.event === 'authChanged')
		)
			.subscribe((e: ISDKPostMessage<ISDKAuthChangeRequest>) => {
				this.updateUserDetails(e.msg.token)
					.then(() => publishParentMessage('authChanged'))
					.catch(e => {
						console.error('Error updating user: ', e);
						publishParentMessage('error', { msg: 'Unauthorized' });
					});
			});
	}
}
